import { isAxiosError } from 'axios';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ViewTransferProofModal from '@/domains/dashboard-management/components/TransferProofModal/ViewTransferProofModal';
import AccordionAffiliate, {
  type AccordionAffiliateItem,
} from '@/domains/mobile-web/components/AccordionAffiliate/AccordionAffiliate';
import TopBarHeader from '@/domains/mobile-web/components/TopBarHeader/TopBarHeader';
import type { CommisionType } from '@/domains/mobile-web/constant/listStatusCommision.constant';
import { useWithdrawals } from '@/domains/mobile-web/hooks/useAffiliate';
import type {
  DuePayout,
  Withdrawal,
  WithdrawalStatus,
} from '@/domains/mobile-web/types/affiliate.types';
import { withdrawalCode } from '@/domains/mobile-web/utils/withdrawal';
import WrapperMobile from '@/shared/components/layout/WrapperMobile/WrapperMobile';
import HeaderMobile from '@/shared/components/ui/HeaderMobile/HeaderMobile';

const STATUS: Record<WithdrawalStatus, CommisionType> = {
  requested: 'pending',
  approved: 'pending',
  processing: 'pending',
  paid: 'completed',
  rejected: 'rejected',
  cancelled: 'cancelled',
};

const toItem = (withdrawal: Withdrawal): AccordionAffiliateItem => ({
  id: withdrawalCode(withdrawal),
  title: 'Komisi Masuk',
  date: withdrawal.requested_at,
  status: STATUS[withdrawal.status],
  amount: Number(withdrawal.amount),
  transferDate: withdrawal.paid_at,
  bankName: withdrawal.bank_name,
  accountName: withdrawal.bank_account_holder,
  accountNumber: withdrawal.bank_account_number,
  transferAmount: Number(withdrawal.amount),
});

// Not transferred yet: dated today, nothing to show for the transfer date or the proof
const dueItem = (due: DuePayout): AccordionAffiliateItem => ({
  id: '-',
  title: 'Komisi Masuk',
  date: new Date().toISOString(),
  status: 'pending',
  amount: Number(due.amount),
  transferDate: null,
  bankName: due.bank_name ?? '-',
  accountName: due.bank_account_holder ?? '-',
  accountNumber: due.bank_account_number ?? '-',
  transferAmount: Number(due.amount),
});

const loadErrorText = (error: Error) =>
  isAxiosError(error) && error.response?.status === 403
    ? 'Halaman affiliate khusus untuk Family Member.'
    : isAxiosError(error) && error.response?.status === 404
      ? 'Akun affiliate tidak ditemukan.'
      : 'Gagal memuat riwayat.';

const HistoryAffiliate = () => {
  const navigate = useNavigate();
  const { data, isLoading, error } = useWithdrawals();
  const [proofUrl, setProofUrl] = useState<string | null>(null);

  return (
    <main className="min-h-screen">
      <HeaderMobile>
        <TopBarHeader type="back" title="Riwayat" onBack={() => navigate(-1)} />
      </HeaderMobile>

      <WrapperMobile className="mb-[120px]">
        {!data || (!data.due && data.rows.length === 0) ? (
          <p className="reg12 text-text-grey text-center py-6">
            {isLoading
              ? 'Memuat...'
              : error
                ? loadErrorText(error)
                : data
                  ? 'Belum ada riwayat.'
                  : 'Gagal memuat riwayat.'}
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            {data.due && <AccordionAffiliate item={dueItem(data.due)} />}
            {data.rows.map((withdrawal) => {
              const url = withdrawal.proof?.url;
              return (
                <AccordionAffiliate
                  key={withdrawal.id}
                  item={toItem(withdrawal)}
                  onProof={url ? () => setProofUrl(url) : undefined}
                />
              );
            })}
          </div>
        )}
      </WrapperMobile>
      <ViewTransferProofModal
        open={proofUrl !== null}
        imageUrl={proofUrl}
        onClose={() => setProofUrl(null)}
      />
    </main>
  );
};

export default HistoryAffiliate;
